import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import xml2js from 'xml2js';

const breakpoints = { mobile: '768px', tablet: '1024px' };

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  padding: 1.5rem 2rem 2rem;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1rem;
  align-items: start;
  max-width: 1500px;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 1rem;
  }
`;

const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CardHead = styled.div`
  padding: 0.95rem 1.15rem;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
`;

const CountBadge = styled.span`
  font-size: 0.72rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-weight: 600;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.75rem 0.85rem 0.5rem;
  padding: 0 0.7rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  &:focus-within { border-color: #2563eb; background: #ffffff; }
  & svg { width: 14px; height: 14px; color: #9ca3af; flex-shrink: 0; }

  input {
    flex: 1;
    border: none;
    background: transparent;
    padding: 0.5rem 0;
    font-size: 0.85rem;
    color: #111827;
    outline: none;
    min-width: 0;
    &::placeholder { color: #9ca3af; }
  }
`;

const ServerList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0.4rem 0.5rem 0.6rem;
  gap: 0.25rem;
  max-height: 600px;
  overflow-y: auto;
`;

const ServerRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 0.7rem;
  background: ${({ selected }) => (selected ? '#eff6ff' : 'transparent')};
  border: 1px solid ${({ selected }) => (selected ? '#bfdbfe' : 'transparent')};
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: all 0.12s ease;

  &:hover {
    background: ${({ selected }) => (selected ? '#dbeafe' : '#f9fafb')};
  }
`;

const ServerLogo = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: ${({ bg }) => bg || '#f3f4f6'};
  color: ${({ color }) => color || '#374151'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 700;
  font-size: 0.7rem;
`;

const ServerInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ServerNameSpan = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ServerTypeSpan = styled.span`
  font-size: 0.72rem;
  color: #6b7280;
  margin-top: 1px;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status }) =>
    status === 'ok' ? '#22c55e' : status === 'warn' ? '#f59e0b' : status === 'error' ? '#ef4444' : '#cbd5e1'};
  flex-shrink: 0;
`;

const AddBtn = styled.button`
  margin: 0.5rem 0.85rem 0.85rem;
  padding: 0.65rem;
  background: #ffffff;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  color: #4b5563;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  &:hover { background: #eff6ff; border-color: #2563eb; color: #2563eb; }
  & svg { width: 14px; height: 14px; }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  animation: ${fadeIn} 0.18s ease both;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`;

const Sub = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.88rem;
  color: #6b7280;
`;

const TopActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  background: #2563eb;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { background: #1d4ed8; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  & svg { width: 14px; height: 14px; }
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.95rem;
  background: #ffffff;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;

  &:hover { background: #f9fafb; border-color: #d1d5db; }
  & svg { width: 14px; height: 14px; }
`;

const SelectedServerCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.85rem 1.1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  align-self: flex-start;
`;

const SelectedServerInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelServerName = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
`;

const SelServerType = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 1px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.85rem;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const StatTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

const StatIconBox = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: ${({ bg }) => bg || '#eff6ff'};
  color: ${({ color }) => color || '#2563eb'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & svg { width: 16px; height: 16px; }
`;

const StatLabel = styled.span`
  font-size: 0.85rem;
  color: #4b5563;
  font-weight: 500;
`;

const StatValue = styled.span`
  font-size: 1.65rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
`;

const Toolbar = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.7rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ToolSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.7rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  flex: 1;
  min-width: 200px;
  max-width: 320px;

  &:focus-within { border-color: #2563eb; background: #ffffff; }
  & svg { width: 14px; height: 14px; color: #9ca3af; }

  input {
    flex: 1;
    border: none;
    background: transparent;
    padding: 0.45rem 0;
    font-size: 0.85rem;
    color: #111827;
    outline: none;
    min-width: 0;
    &::placeholder { color: #9ca3af; }
  }
`;

const ToolSelect = styled.select`
  padding: 0.45rem 1.85rem 0.45rem 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 0.65rem center;
  background-size: 12px 12px;

  &:hover { border-color: #d1d5db; }
  &:focus { outline: none; border-color: #2563eb; }
`;

const Spacer = styled.div`
  flex: 1;
`;

const SmallIconBtn = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;

  &:hover { color: #2563eb; border-color: #2563eb; background: #eff6ff; }
  & svg { width: 14px; height: 14px; }
`;

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const TableEl = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;
  background: #fafbfc;

  &:first-child { width: 44px; padding-left: 1.1rem; }
  &:last-child { padding-right: 1.1rem; width: 130px; }
`;

const Td = styled.td`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.85rem;
  color: #374151;
  vertical-align: top;

  &:first-child { padding-left: 1.1rem; width: 44px; }
  &:last-child { padding-right: 1.1rem; }

  tr:last-child & { border-bottom: none; }
`;

const RowTr = styled.tr`
  &:hover { background: #fafbfc; }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const TaskName = styled.span`
  font-size: 0.88rem;
  font-weight: 700;
  color: #111827;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${({ bg }) => bg || '#f3f4f6'};
  color: ${({ color }) => color || '#6b7280'};
`;

const CmdText = styled.code`
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.78rem;
  color: #4b5563;
  background: #f9fafb;
  padding: 0.2rem 0.5rem;
  border-radius: 5px;
  border: 1px solid #f3f4f6;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CellLine1 = styled.span`
  font-size: 0.85rem;
  color: #111827;
  font-weight: 600;
  display: block;
`;

const CellLine2 = styled.span`
  font-size: 0.74rem;
  color: #6b7280;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  display: block;
  margin-top: 2px;
`;

const ResultText = styled.span`
  font-size: 0.74rem;
  font-weight: 600;
  color: ${({ tone }) => (tone === 'success' ? '#16a34a' : tone === 'failed' ? '#dc2626' : '#6b7280')};
  display: block;
  margin-top: 2px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ on }) => (on ? '#dcfce7' : '#f3f4f6')};
  color: ${({ on }) => (on ? '#166534' : '#6b7280')};
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ on }) => (on ? '#22c55e' : '#9ca3af')};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.25rem;
  justify-content: flex-end;
`;

const IconAct = styled.button`
  width: 30px;
  height: 30px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.12s ease;

  &:hover { color: #2563eb; border-color: #bfdbfe; background: #eff6ff; }
  & svg { width: 13px; height: 13px; }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.1rem;
  border-top: 1px solid #f3f4f6;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.82rem;
`;

const PageInfo = styled.span`
  color: #6b7280;
`;

const PageNums = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const PageNumBtn = styled.button`
  min-width: 30px;
  height: 30px;
  padding: 0 0.45rem;
  border: 1px solid ${({ active }) => (active ? '#2563eb' : '#e5e7eb')};
  background: ${({ active }) => (active ? '#2563eb' : '#ffffff')};
  color: ${({ active }) => (active ? '#ffffff' : '#374151')};
  border-radius: 7px;
  font-size: 0.82rem;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ active }) => (active ? '#1d4ed8' : '#f3f4f6')};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  & svg { width: 12px; height: 12px; }
`;

const PerPageSelect = styled(ToolSelect)`
  padding: 0.4rem 1.6rem 0.4rem 0.65rem;
  font-size: 0.8rem;
`;

// === icons ===
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4" /></svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.83z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const InactiveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

// === helpers ===
function getServerStyle(type, name) {
  const t = (type || '').toLowerCase();
  const n = (name || '').toLowerCase();
  if (t.includes('hetzner') || n.includes('hetzner')) return { bg: '#fef2f2', color: '#d50c2d', short: 'H', label: 'Hetzner' };
  if (t.includes('cloudflare') || t.includes('r2')) return { bg: '#fff7ed', color: '#f38020', short: 'CF', label: 'Cloudflare R2' };
  if (t === 'aws_s3' || t.includes('aws')) return { bg: '#fff7ed', color: '#ff9900', short: 'AWS', label: 'AWS S3' };
  if (n.includes('cinetree') || n.includes('skynet') || t.includes('nas')) return { bg: '#f5f3ff', color: '#7c3aed', short: 'NAS', label: 'NAS' };
  if (t.includes('ftp')) return { bg: '#ecfeff', color: '#0891b2', short: 'FI', label: 'FTP' };
  if (t === 'default') return { bg: '#f5f3ff', color: '#7c3aed', short: 'FS', label: 'default' };
  return { bg: '#f3f4f6', color: '#374151', short: (name || '?').slice(0, 2).toUpperCase(), label: type || 'Storage' };
}

function getTagStyle(tag) {
  switch (tag) {
    case '백업': return { bg: '#fef3c7', color: '#92400e' };
    case '유지보수': return { bg: '#dbeafe', color: '#1e40af' };
    case '리포트': return { bg: '#f5f3ff', color: '#6d28d9' };
    case '미디어': return { bg: '#cffafe', color: '#155e75' };
    case '테스트': return { bg: '#f3f4f6', color: '#4b5563' };
    case '동기화': return { bg: '#dcfce7', color: '#166534' };
    default: return { bg: '#f3f4f6', color: '#6b7280' };
  }
}

// 서버별 모의 크론 잡 (각 서버에 대해 약간 다른 작업 노출)
function genMockJobs(serverName) {
  return [
    { id: `${serverName}-1`, name: '로그 백업', tag: '백업', cmd: '/usr/local/bin/backup.sh', schedule: '매일 02:00', cronExpr: '0 2 * * *', lastRun: '2025.05.08 02:00:01', lastResult: 'success', lastDuration: '2.1초', nextRun: '2025.05.09 02:00:00', nextIn: '23시간 42분 후', status: 'active' },
    { id: `${serverName}-2`, name: '임시파일 정리', tag: '유지보수', cmd: '/usr/local/bin/cleanup.sh', schedule: '매일 03:30', cronExpr: '30 3 * * *', lastRun: '2025.05.08 03:30:11', lastResult: 'success', lastDuration: '1.5초', nextRun: '2025.05.09 03:30:00', nextIn: '1일 후', status: 'active' },
    { id: `${serverName}-3`, name: '사용량 리포트', tag: '리포트', cmd: '/usr/local/bin/report.sh', schedule: '매주 일요일 09:00', cronExpr: '0 9 * * 0', lastRun: '2025.05.05 09:00:05', lastResult: 'success', lastDuration: '3.2초', nextRun: '2025.05.12 09:00:00', nextIn: '4일 후', status: 'active' },
    { id: `${serverName}-4`, name: 'DB 백업', tag: '백업', cmd: '/usr/local/bin/db_backup.sh', schedule: '매일 01:00', cronExpr: '0 1 * * *', lastRun: '2025.05.08 01:00:09', lastResult: 'failed', lastDuration: '5.6초', nextRun: '2025.05.09 01:00:00', nextIn: '22시간 42분 후', status: 'active' },
    { id: `${serverName}-5`, name: '썸네일 생성', tag: '미디어', cmd: '/usr/local/bin/thumbnail.sh', schedule: '매시간 00분', cronExpr: '0 * * * *', lastRun: '2025.05.08 11:00:00', lastResult: 'success', lastDuration: '0.8초', nextRun: '2025.05.08 12:00:00', nextIn: '42분 후', status: 'active' },
    { id: `${serverName}-6`, name: '비활성 샘플 작업', tag: '테스트', cmd: '/usr/local/bin/sample.sh', schedule: '매월 1일 00:00', cronExpr: '0 0 1 * *', lastRun: '2025.04.01 00:00:00', lastResult: 'success', lastDuration: '0.3초', nextRun: null, nextIn: null, status: 'inactive' },
    { id: `${serverName}-7`, name: '인덱스 동기화', tag: '동기화', cmd: '/usr/local/bin/sync_index.sh', schedule: '매 30분', cronExpr: '*/30 * * * *', lastRun: '2025.05.08 11:30:00', lastResult: 'success', lastDuration: '4.1초', nextRun: '2025.05.08 12:00:00', nextIn: '12분 후', status: 'active' },
    { id: `${serverName}-8`, name: '로그 압축', tag: '유지보수', cmd: '/usr/local/bin/compress_logs.sh', schedule: '매일 04:00', cronExpr: '0 4 * * *', lastRun: '2025.05.08 04:00:02', lastResult: 'success', lastDuration: '6.8초', nextRun: '2025.05.09 04:00:00', nextIn: '1일 후', status: 'active' },
    { id: `${serverName}-9`, name: '권한 점검', tag: '유지보수', cmd: '/usr/local/bin/perm_check.sh', schedule: '매주 월요일 06:00', cronExpr: '0 6 * * 1', lastRun: '2025.05.05 06:00:12', lastResult: 'success', lastDuration: '12.4초', nextRun: '2025.05.12 06:00:00', nextIn: '4일 후', status: 'active' },
    { id: `${serverName}-10`, name: '월간 리포트', tag: '리포트', cmd: '/usr/local/bin/monthly_report.sh', schedule: '매월 1일 08:00', cronExpr: '0 8 1 * *', lastRun: '2025.05.01 08:00:00', lastResult: 'success', lastDuration: '8.9초', nextRun: '2025.06.01 08:00:00', nextIn: '24일 후', status: 'active' },
    { id: `${serverName}-11`, name: '성능 메트릭', tag: '리포트', cmd: '/usr/local/bin/metrics.sh', schedule: '매시간 30분', cronExpr: '30 * * * *', lastRun: '2025.05.08 11:30:00', lastResult: 'success', lastDuration: '0.5초', nextRun: '2025.05.08 12:30:00', nextIn: '1시간 12분 후', status: 'active' },
    { id: `${serverName}-12`, name: '오래된 캐시 삭제', tag: '유지보수', cmd: '/usr/local/bin/cache_purge.sh', schedule: '매일 05:00', cronExpr: '0 5 * * *', lastRun: '2025.04.10 05:00:00', lastResult: 'success', lastDuration: '0.7초', nextRun: null, nextIn: null, status: 'inactive' },
  ];
}

const Cron = () => {
  const [list, setList] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [serverSearch, setServerSearch] = useState('');
  const [statuses, setStatuses] = useState({});

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // 서버 목록 로드
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/ServerList.xml');
        if (!res.ok) throw new Error('Failed');
        const xml = await res.text();
        xml2js.parseString(xml, (err, result) => {
          if (cancelled || err) return;
          const nodes = result?.servers?.node || [];
          const parsed = nodes.map((it) => ({
            name: it.name?.[0] || '',
            type: it.type?.[0] || '',
            address: it.address?.[0] || '',
            fileList: it.fileList?.[0] || '',
          }));
          setList(parsed);
          setSelectedIdx(parsed.length > 0 ? 0 : null);
        });
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // 서버 상태 ping
  useEffect(() => {
    list.forEach(async (svr) => {
      if (!svr.fileList || statuses[svr.name]) return;
      const t = Date.now();
      try {
        const res = await fetch(svr.fileList);
        if (!res.ok) throw new Error();
        const elapsed = Date.now() - t;
        setStatuses((p) => ({ ...p, [svr.name]: elapsed > 4000 ? 'warn' : 'ok' }));
      } catch {
        setStatuses((p) => ({ ...p, [svr.name]: 'error' }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const sel = selectedIdx != null ? list[selectedIdx] : null;
  const selStyle = sel ? getServerStyle(sel.type, sel.name) : null;

  const jobs = useMemo(() => sel ? genMockJobs(sel.name) : [], [sel]);

  const stats = useMemo(() => {
    const total = jobs.length;
    const active = jobs.filter((j) => j.status === 'active').length;
    const inactive = jobs.filter((j) => j.status === 'inactive').length;
    const failed = jobs.filter((j) => j.lastResult === 'failed').length;
    return { total, active, inactive, failed };
  }, [jobs]);

  const filtered = useMemo(() => {
    let r = jobs;
    if (search) {
      const q = search.toLowerCase();
      r = r.filter((j) => `${j.name} ${j.cmd}`.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') r = r.filter((j) => j.status === statusFilter);
    return r;
  }, [jobs, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const startIdx = (page - 1) * perPage;
  const visible = filtered.slice(startIdx, startIdx + perPage);

  useEffect(() => { setPage(1); }, [search, statusFilter, userFilter, perPage, selectedIdx]);

  const filteredServers = useMemo(() => {
    if (!serverSearch) return list.map((s, i) => ({ s, i }));
    const q = serverSearch.toLowerCase();
    return list.map((s, i) => ({ s, i })).filter(({ s }) => `${s.name} ${s.type}`.toLowerCase().includes(q));
  }, [list, serverSearch]);

  return (
    <Page>
      {/* === 좌측 서버 목록 === */}
      <Card>
        <CardHead>
          <CardTitle>서버 목록</CardTitle>
          <CountBadge>{list.length}</CountBadge>
        </CardHead>
        <SearchBox>
          <SearchIcon />
          <input type="text" placeholder="이름, 타입, 주소로 검색" value={serverSearch} onChange={(e) => setServerSearch(e.target.value)} />
        </SearchBox>
        <ServerList>
          {filteredServers.map(({ s, i }) => {
            const style = getServerStyle(s.type, s.name);
            const status = statuses[s.name];
            return (
              <ServerRow key={i} selected={i === selectedIdx ? 1 : 0} onClick={() => setSelectedIdx(i)}>
                <ServerLogo bg={style.bg} color={style.color}>{style.short}</ServerLogo>
                <ServerInfo>
                  <ServerNameSpan>{s.name || `서버 ${i + 1}`}</ServerNameSpan>
                  <ServerTypeSpan>{s.type || '-'}</ServerTypeSpan>
                </ServerInfo>
                <StatusDot status={status} />
              </ServerRow>
            );
          })}
        </ServerList>
        <AddBtn><PlusIcon /> 서버 추가</AddBtn>
      </Card>

      {/* === 우측 메인 === */}
      <Main>
        <Head>
          <div>
            <Title>Cron 관리</Title>
            <Sub>선택된 서버의 크론 작업을 관리합니다.</Sub>
          </div>
          <TopActions>
            <GhostBtn><RefreshIcon /> 새로고침</GhostBtn>
            <PrimaryBtn><PlusIcon /> 크론 추가</PrimaryBtn>
          </TopActions>
        </Head>

        <StatGrid>
          <StatCard>
            <StatTopRow>
              <StatIconBox bg="#eff6ff" color="#2563eb"><ClockIcon /></StatIconBox>
              <StatLabel>전체 작업</StatLabel>
            </StatTopRow>
            <StatValue>{stats.total}개</StatValue>
          </StatCard>
          <StatCard>
            <StatTopRow>
              <StatIconBox bg="#dcfce7" color="#16a34a"><PlayIcon /></StatIconBox>
              <StatLabel>활성 작업</StatLabel>
            </StatTopRow>
            <StatValue>{stats.active}개</StatValue>
          </StatCard>
          <StatCard>
            <StatTopRow>
              <StatIconBox bg="#f3f4f6" color="#6b7280"><InactiveIcon /></StatIconBox>
              <StatLabel>비활성 작업</StatLabel>
            </StatTopRow>
            <StatValue>{stats.inactive}개</StatValue>
          </StatCard>
          <StatCard>
            <StatTopRow>
              <StatIconBox bg="#fef2f2" color="#dc2626"><AlertIcon /></StatIconBox>
              <StatLabel>실패 작업 (최근 24시간)</StatLabel>
            </StatTopRow>
            <StatValue>{stats.failed}개</StatValue>
          </StatCard>
        </StatGrid>

        <Toolbar>
          <ToolSearch>
            <SearchIcon />
            <input type="text" placeholder="작업명 또는 명령어 검색" value={search} onChange={(e) => setSearch(e.target.value)} />
          </ToolSearch>
          <ToolSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">전체 상태</option>
            <option value="active">활성</option>
            <option value="inactive">비활성</option>
          </ToolSelect>
          <ToolSelect value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
            <option value="all">전체 사용자</option>
            <option value="root">root</option>
            <option value="admin">admin</option>
          </ToolSelect>
          <Spacer />
          <GhostBtn><TagIcon /> 태그 필터</GhostBtn>
          <SmallIconBtn aria-label="설정"><SettingsIcon /></SmallIconBtn>
        </Toolbar>

        <TableCard>
          <TableEl>
            <thead>
              <tr>
                <Th>
                  <input type="checkbox" style={{ accentColor: '#2563eb', cursor: 'pointer' }} />
                </Th>
                <Th>작업명</Th>
                <Th>명령어</Th>
                <Th>주기 (Cron 표현식)</Th>
                <Th>마지막 실행</Th>
                <Th>다음 실행</Th>
                <Th>상태</Th>
                <Th>작업</Th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>표시할 작업이 없습니다.</td></tr>
              ) : visible.map((j) => {
                const tagStyle = getTagStyle(j.tag);
                return (
                  <RowTr key={j.id}>
                    <Td><input type="checkbox" style={{ accentColor: '#2563eb', cursor: 'pointer' }} /></Td>
                    <Td>
                      <NameCell>
                        <TaskName>{j.name}</TaskName>
                        <Tag bg={tagStyle.bg} color={tagStyle.color}>{j.tag}</Tag>
                      </NameCell>
                    </Td>
                    <Td><CmdText>{j.cmd}</CmdText></Td>
                    <Td>
                      <CellLine1>{j.schedule}</CellLine1>
                      <CellLine2>{j.cronExpr}</CellLine2>
                    </Td>
                    <Td>
                      <CellLine1>{j.lastRun}</CellLine1>
                      <ResultText tone={j.lastResult}>
                        {j.lastResult === 'success' ? `성공 (${j.lastDuration})` : `실패 (${j.lastDuration})`}
                      </ResultText>
                    </Td>
                    <Td>
                      {j.nextRun ? (
                        <>
                          <CellLine1>{j.nextRun}</CellLine1>
                          <CellLine2>{j.nextIn}</CellLine2>
                        </>
                      ) : '-'}
                    </Td>
                    <Td>
                      <StatusPill on={j.status === 'active' ? 1 : 0}>
                        {j.status === 'active' ? '활성' : '비활성'}
                      </StatusPill>
                    </Td>
                    <Td>
                      <RowActions>
                        <IconAct aria-label="실행"><PlayIcon /></IconAct>
                        <IconAct aria-label="수정"><EditIcon /></IconAct>
                        <IconAct aria-label="더보기"><MoreIcon /></IconAct>
                      </RowActions>
                    </Td>
                  </RowTr>
                );
              })}
            </tbody>
          </TableEl>
          <PaginationBar>
            <PageInfo>전체 {filtered.length}개 중 {filtered.length === 0 ? 0 : startIdx + 1}-{Math.min(startIdx + perPage, filtered.length)} 표시</PageInfo>
            <PageNums>
              <PageNumBtn onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}><ChevronLeft /></PageNumBtn>
              {Array.from({ length: totalPages }).map((_, i) => (
                <PageNumBtn key={i} active={page === i + 1 ? 1 : 0} onClick={() => setPage(i + 1)}>{i + 1}</PageNumBtn>
              ))}
              <PageNumBtn onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}><ChevronRight /></PageNumBtn>
            </PageNums>
            <PerPageSelect value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
              <option value={10}>10개씩 보기</option>
              <option value={20}>20개씩 보기</option>
              <option value={50}>50개씩 보기</option>
            </PerPageSelect>
          </PaginationBar>
        </TableCard>
      </Main>
    </Page>
  );
};

export default Cron;
